/**
 * 選択肢項目（ラジオボタン・プルダウン）のユーティリティ（#3075）。
 *
 * 選択肢は「表示名（label）」と「出力値（value）」の組で持つ。
 * 画面には表示名を出し、result_data・CSVには出力値を入れることで、
 * 「画面には有効、CSVには1」といった基幹システム連携に対応する。
 *
 * ここの正規化ルールは Functions 側の normalizeChoiceValues と揃えること。
 * 揃っていないと、フロントで候補内と判定した値がOCR後処理では候補外になる（またはその逆）。
 */

import { CHOICE_MAX_COUNT } from '../../constants/enum';
import { itemChoiceErrorMessage } from '../../constants/itemValidation';
import { ItemChoiceType } from '../../types/common';

/**
 * 選択肢の出力値を解決する。
 * 出力値が未設定（空文字）なら表示名をそのまま出力値として使う。
 */
export const resolveChoiceValue = (choice: ItemChoiceType): string => {
  // 型は string だが Firestore 由来のため数値が入ることがある（#3260）
  const trimmed = String(choice.value ?? '').trim();
  return trimmed !== '' ? trimmed : String(choice.label ?? '').trim();
};

/**
 * 表記ゆれ吸収のための正規化。
 *
 * 意味の推測はせず、機械的な表記の揺れだけを吸収する。
 * 部分一致・かな漢字変換・類似度による寄せは意図的に行わない（#3075）。
 * これらは「非課税」を「課税」と判定するような、意味が反転する誤りを生むため。
 */
export const normalizeChoiceText = (input: string): string => {
  if (input === '') return '';

  let s = input;

  // 半角カナ → 全角カナ（濁点・半濁点は1文字に合成する）
  s = toFullWidthKana(s);

  // 全角英数 → 半角
  s = s.replace(/[Ａ-Ｚａ-ｚ０-９]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));

  // 空白をすべて除去。\s は全角スペース（U+3000）も含む
  s = s.replace(/\s/g, '');

  // 英字の大文字・小文字を統一
  return s.toLowerCase();
};

// 半角カナ → 全角カナの対応表（濁点・半濁点つきを先に引くこと）
const halfToFullKanaVoiced: Record<string, string> = {
  ｶﾞ: 'ガ',
  ｷﾞ: 'ギ',
  ｸﾞ: 'グ',
  ｹﾞ: 'ゲ',
  ｺﾞ: 'ゴ',
  ｻﾞ: 'ザ',
  ｼﾞ: 'ジ',
  ｽﾞ: 'ズ',
  ｾﾞ: 'ゼ',
  ｿﾞ: 'ゾ',
  ﾀﾞ: 'ダ',
  ﾁﾞ: 'ヂ',
  ﾂﾞ: 'ヅ',
  ﾃﾞ: 'デ',
  ﾄﾞ: 'ド',
  ﾊﾞ: 'バ',
  ﾋﾞ: 'ビ',
  ﾌﾞ: 'ブ',
  ﾍﾞ: 'ベ',
  ﾎﾞ: 'ボ',
  ﾊﾟ: 'パ',
  ﾋﾟ: 'ピ',
  ﾌﾟ: 'プ',
  ﾍﾟ: 'ペ',
  ﾎﾟ: 'ポ',
  ｳﾞ: 'ヴ',
};

const halfToFullKana: Record<string, string> = {
  ｱ: 'ア',
  ｲ: 'イ',
  ｳ: 'ウ',
  ｴ: 'エ',
  ｵ: 'オ',
  ｶ: 'カ',
  ｷ: 'キ',
  ｸ: 'ク',
  ｹ: 'ケ',
  ｺ: 'コ',
  ｻ: 'サ',
  ｼ: 'シ',
  ｽ: 'ス',
  ｾ: 'セ',
  ｿ: 'ソ',
  ﾀ: 'タ',
  ﾁ: 'チ',
  ﾂ: 'ツ',
  ﾃ: 'テ',
  ﾄ: 'ト',
  ﾅ: 'ナ',
  ﾆ: 'ニ',
  ﾇ: 'ヌ',
  ﾈ: 'ネ',
  ﾉ: 'ノ',
  ﾊ: 'ハ',
  ﾋ: 'ヒ',
  ﾌ: 'フ',
  ﾍ: 'ヘ',
  ﾎ: 'ホ',
  ﾏ: 'マ',
  ﾐ: 'ミ',
  ﾑ: 'ム',
  ﾒ: 'メ',
  ﾓ: 'モ',
  ﾔ: 'ヤ',
  ﾕ: 'ユ',
  ﾖ: 'ヨ',
  ﾗ: 'ラ',
  ﾘ: 'リ',
  ﾙ: 'ル',
  ﾚ: 'レ',
  ﾛ: 'ロ',
  ﾜ: 'ワ',
  ｦ: 'ヲ',
  ﾝ: 'ン',
  ｧ: 'ァ',
  ｨ: 'ィ',
  ｩ: 'ゥ',
  ｪ: 'ェ',
  ｫ: 'ォ',
  ｬ: 'ャ',
  ｭ: 'ュ',
  ｮ: 'ョ',
  ｯ: 'ッ',
  '｡': '。',
  '､': '、',
  ｰ: 'ー',
  '｢': '「',
  '｣': '」',
  '･': '・',
};

const toFullWidthKana = (input: string): string => {
  let result = '';
  for (let i = 0; i < input.length; i += 1) {
    const pair = input.slice(i, i + 2);
    if (halfToFullKanaVoiced[pair] !== undefined) {
      result += halfToFullKanaVoiced[pair];
      i += 1; // 濁点ぶんを読み飛ばす
      continue;
    }
    const ch = input[i];
    result += halfToFullKana[ch] ?? ch;
  }
  return result;
};

/**
 * AIの回答（またはユーザー入力）が、どの選択肢に該当するかを判定する。
 *
 * 完全一致 → 正規化後の一致 の順に試し、いずれも外れたら null を返す。
 * 推測による寄せは行わないため、該当なしは素直に null（＝空欄）になる。
 */
export const findMatchingChoice = (
  value: string,
  choices: ItemChoiceType[],
): ItemChoiceType | null => {
  // 型は string だが Firestore 由来のため数値が入ることがある（#3260）
  const trimmed = String(value ?? '').trim();
  if (trimmed === '' || choices.length === 0) return null;

  // 1. 表示名と完全一致
  const byLabel = choices.find(choice => String(choice.label ?? '').trim() === trimmed);
  if (byLabel) return byLabel;

  // 2. 出力値と完全一致
  const byValue = choices.find(choice => resolveChoiceValue(choice) === trimmed);
  if (byValue) return byValue;

  // 3. 正規化後の一致（表記ゆれの吸収）
  const normalized = normalizeChoiceText(trimmed);
  if (normalized === '') return null;

  const byNormalizedLabel = choices.find(
    choice => normalizeChoiceText(String(choice.label ?? '')) === normalized,
  );
  if (byNormalizedLabel) return byNormalizedLabel;

  return (
    choices.find(choice => normalizeChoiceText(resolveChoiceValue(choice)) === normalized) ?? null
  );
};

/**
 * 出力値（result_data に入っている値）を、画面表示用の表示名に変換する。
 * 候補に該当しない場合は元の値をそのまま返す（既存データを消さないため）。
 */
export const resolveChoiceLabel = (value: string, choices: ItemChoiceType[]): string => {
  const matched = findMatchingChoice(value, choices);
  return matched ? matched.label : value;
};

/**
 * 選択肢設定のバリデーション。
 * エラーがなければ null を返す。
 */
export const validateChoices = (choices: ItemChoiceType[]): string | null => {
  if (choices.length === 0) return itemChoiceErrorMessage.empty;
  if (choices.length > CHOICE_MAX_COUNT) {
    return itemChoiceErrorMessage.maxCount(CHOICE_MAX_COUNT);
  }

  const labels = choices.map(choice => String(choice.label ?? '').trim());
  if (labels.some(label => label === '')) return itemChoiceErrorMessage.emptyLabel;
  if (new Set(labels).size !== labels.length) return itemChoiceErrorMessage.duplicatedLabel;

  // 出力値は未設定なら表示名にフォールバックするため、解決後の値で重複を判定する。
  // 「表示名=1・出力値なし」と「表示名=有効・出力値=1」の衝突もここで検出できる。
  const values = choices.map(choice => resolveChoiceValue(choice));
  if (new Set(values).size !== values.length) return itemChoiceErrorMessage.duplicatedValue;

  return null;
};

/**
 * 選択肢を保存用に整える。前後の空白を除去する。
 */
export const sanitizeChoices = (choices: ItemChoiceType[]): ItemChoiceType[] =>
  choices.map(choice => ({
    label: String(choice.label ?? '').trim(),
    value: String(choice.value ?? '').trim(),
  }));

/**
 * 外部由来の値（Firestore・JSON取り込み）を ItemChoiceType[] に正規化する。
 *
 * 手書きJSONでの簡易記法として、文字列配列（["有", "無"]）も受け付ける。
 * 表示名が空の要素は取り込まず、上限を超えた分は切り捨てる。
 */
export const normalizeChoiceList = (input: unknown): ItemChoiceType[] => {
  if (!Array.isArray(input)) return [];

  const result: ItemChoiceType[] = [];
  for (const entry of input) {
    if (result.length >= CHOICE_MAX_COUNT) break;

    // 簡易記法: ["有", "無"]
    if (typeof entry === 'string') {
      const label = entry.trim();
      if (label !== '') result.push({ label, value: '' });
      continue;
    }

    if (entry === null || typeof entry !== 'object') continue;

    const record = entry as Record<string, unknown>;
    const rawLabel = record.label;
    // 数値で書かれた表示名・出力値も文字列として受け付ける
    const label =
      typeof rawLabel === 'string'
        ? rawLabel.trim()
        : typeof rawLabel === 'number'
          ? String(rawLabel)
          : '';
    if (label === '') continue;

    const rawValue = record.value;
    const value =
      typeof rawValue === 'string'
        ? rawValue.trim()
        : typeof rawValue === 'number'
          ? String(rawValue)
          : '';

    result.push({ label, value });
  }

  return result;
};
